// src/components/admin/AdminInquiries.js
//
// 運営側の問い合わせ管理。一覧 → スレッドを開いて返信。
//
// 【表示ルール】
//   会員には送信者を常に「運営」と見せる（senderId は 'admin' 固定）。
//   ただし社内向けに operatorId を保存し、この画面でだけ
//   「◯◯が返信」と分かるようにしている。担当の引き継ぎに必要なため。
//
// 【コスト対策】
//   - 一覧は updatedAt の降順で limit(50)。609件を読むことはない。
//   - スレッドを開いたときだけメッセージを購読し、閉じたら解除する。
//   - メッセージは新しい順に limit(50)。「以前のメッセージ」で追加読み込み。
//   - 未読はルーム側のカウンタを見る（メッセージを数えない）。
//
// 【未読の考え方】
//   unread.admin … 運営がまだ見ていない件数
//   unread.{userId} … その会員がまだ見ていない件数
//   運営3名で共有するため、誰か1人が開けば全員分の未読が消える。
//   「誰が対応したか」は operatorId で追える。
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import {
    collection, doc, query, orderBy, limit,
    onSnapshot, addDoc, updateDoc, setDoc, serverTimestamp, increment
} from 'firebase/firestore';
import { COLORS } from '../../theme';

const LIST_LIMIT = 50;
const MSG_PAGE = 50;

const fmtDateTime = (ts) => {
    if (!ts || typeof ts.toDate !== 'function') return '';
    const d = ts.toDate();
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return sameDay ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
};

// rooms / isLoading は Admin.js から受け取る。
//   タブの未読バッジでも同じ一覧が必要なため、購読を1本にまとめている。
//   ここで独自に購読すると、タブを開くたびに読み取りが二重になる。
const AdminInquiries = ({ rooms = [], isLoading = false, currentUserId = null, currentUserName = '' }) => {
    const [openRoom, setOpenRoom] = useState(null);      // 開いているルーム
    const [messages, setMessages] = useState([]);
    const [msgLimit, setMsgLimit] = useState(MSG_PAGE);
    const [hasMore, setHasMore] = useState(false);
    const [reply, setReply] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [onlyUnread, setOnlyUnread] = useState(false);
    const endRef = useRef(null);

    // ---- スレッドの購読（開いている間だけ） ------------------------------
    useEffect(() => {
        if (!openRoom) { setMessages([]); return; }
        const q = query(
            collection(db, 'chats', openRoom.id, 'messages'),
            orderBy('createdAt', 'desc'),
            limit(msgLimit)
        );
        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.reverse();
            setMessages(list);
            setHasMore(snap.docs.length >= msgLimit);
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
        }, (err) => console.error('メッセージ取得エラー:', err));
        return () => unsub();
    }, [openRoom, msgLimit]);

    // ---- 開いたら運営側の未読を0にする ------------------------------------
    const handleOpen = async (room) => {
        setMsgLimit(MSG_PAGE);
        setOpenRoom(room);
        const unreadCount = (room.unread || {}).admin || 0;
        if (unreadCount > 0) {
            try {
                // updateDoc はドット記法をフィールドパスとして解釈するので、
                //   'unread.admin' の書き方で問題ない（setDoc とは挙動が違う）。
                await updateDoc(doc(db, 'chats', room.id), { 'unread.admin': 0 });
            } catch (e) {
                console.error('既読処理に失敗:', e);
            }
        }
    };

    // ---- 返信 -----------------------------------------------------------
    const handleReply = async () => {
        const text = reply.trim();
        if (!text || isSending || !openRoom) return;
        const targetUserId = (openRoom.members || []).find(m => m !== 'admin');
        if (!targetUserId) { alert('送信先の会員が特定できません'); return; }

        setIsSending(true);
        setReply('');
        try {
            await addDoc(collection(db, 'chats', openRoom.id, 'messages'), {
                senderId: 'admin',            // ★会員には「運営」と見える
                operatorId: currentUserId,    // ★社内向け。誰が返信したか
                operatorName: currentUserName,
                text,
                createdAt: serverTimestamp()
            });

            // ★ unread は入れ子マップで書く。
            //   setDoc はドット記法を解釈しないため、'unread.xxx' と書くと
            //   その名前のフィールドが1つできてしまう。
            await setDoc(doc(db, 'chats', openRoom.id), {
                lastMessage: text.slice(0, 60),
                lastSenderId: 'admin',
                updatedAt: serverTimestamp(),
                unread: {
                    admin: 0,
                    [targetUserId]: increment(1)
                }
            }, { merge: true });

            setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
        } catch (e) {
            console.error('返信エラー:', e);
            alert('送信できませんでした。もう一度お試しください。');
            setReply(text);
        } finally {
            setIsSending(false);
        }
    };

    const unreadTotal = rooms.reduce((sum, r) => sum + (((r.unread || {}).admin) || 0), 0);
    const visibleRooms = onlyUnread
        ? rooms.filter(r => (((r.unread || {}).admin) || 0) > 0)
        : rooms;

    // ==================== スレッド画面 ====================
    if (openRoom) {
        const partnerId = (openRoom.members || []).find(m => m !== 'admin');
        const partnerName = (openRoom.memberNames || {})[partnerId] || partnerId || 'メンバー';

        return (
            <div className="card" style={{ padding: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <button
                        onClick={() => setOpenRoom(null)}
                        style={{ background: '#f5f5f5', border: 'none', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                        ← 一覧
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {partnerName}
                        </div>
                        <div style={{ fontSize: '10px', color: '#aaa', wordBreak: 'break-all' }}>{partnerId}</div>
                    </div>
                </div>

                <div style={{
                    background: '#7fb5b5', borderRadius: '10px', padding: '12px',
                    maxHeight: '55vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px'
                }}>
                    {hasMore && (
                        <button
                            onClick={() => setMsgLimit(n => n + MSG_PAGE)}
                            style={{
                                background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '20px',
                                padding: '6px 14px', fontSize: '11px', color: '#555', cursor: 'pointer', alignSelf: 'center'
                            }}
                        >
                            以前のメッセージを表示
                        </button>
                    )}

                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', color: 'white', fontSize: '12px', padding: '30px 0' }}>
                            メッセージがありません
                        </div>
                    )}

                    {messages.map(m => {
                        const fromAdmin = m.senderId === 'admin';
                        return (
                            <div key={m.id} style={{ display: 'flex', justifyContent: fromAdmin ? 'flex-end' : 'flex-start' }}>
                                <div style={{ maxWidth: '78%' }}>
                                    <div style={{
                                        padding: '9px 12px',
                                        borderRadius: fromAdmin ? '15px 15px 0 15px' : '15px 15px 15px 0',
                                        background: fromAdmin ? '#85e249' : '#fff',
                                        color: '#333', fontSize: '13px', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                    }}>
                                        {m.text}
                                    </div>
                                    <div style={{
                                        fontSize: '9px', color: 'rgba(255,255,255,0.9)', marginTop: '2px',
                                        textAlign: fromAdmin ? 'right' : 'left'
                                    }}>
                                        {/* ★社内向け表示。会員側には出ない */}
                                        {fromAdmin && m.operatorName ? `${m.operatorName} / ` : ''}
                                        {fmtDateTime(m.createdAt)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={endRef} />
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <textarea
                        value={reply}
                        onChange={e => setReply(e.target.value)}
                        placeholder="返信を入力...（会員には「運営」と表示されます）"
                        rows={3}
                        style={{
                            flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ccc',
                            fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
                        }}
                    />
                    <button
                        onClick={handleReply}
                        disabled={!reply.trim() || isSending}
                        style={{
                            background: !reply.trim() || isSending ? '#ccc' : COLORS.primary,
                            color: 'white', border: 'none', borderRadius: '8px',
                            padding: '0 18px', fontWeight: 'bold', fontSize: '13px',
                            cursor: reply.trim() && !isSending ? 'pointer' : 'default', flexShrink: 0
                        }}
                    >
                        {isSending ? '...' : '返信'}
                    </button>
                </div>
            </div>
        );
    }

    // ==================== 一覧画面 ====================
    return (
        <div className="card" style={{ padding: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                    💬 問い合わせ
                    {unreadTotal > 0 && (
                        <span style={{
                            marginLeft: '8px', fontSize: '11px', background: '#d32f2f', color: 'white',
                            padding: '2px 8px', borderRadius: '10px'
                        }}>
                            未読 {unreadTotal}
                        </span>
                    )}
                </h3>
                <label style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={onlyUnread}
                        onChange={e => setOnlyUnread(e.target.checked)}
                        style={{ width: 'auto' }}
                    />
                    未読のみ
                </label>
            </div>

            {isLoading ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '30px', fontSize: '13px' }}>読み込み中...</div>
            ) : visibleRooms.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px', fontSize: '13px' }}>
                    {onlyUnread ? '未読の問い合わせはありません' : '問い合わせはまだありません'}
                </div>
            ) : (
                visibleRooms.map(room => {
                    const partnerId = (room.members || []).find(m => m !== 'admin');
                    const partnerName = (room.memberNames || {})[partnerId] || partnerId || 'メンバー';
                    const unread = ((room.unread || {}).admin) || 0;
                    const waiting = room.lastSenderId && room.lastSenderId !== 'admin';
                    return (
                        <div
                            key={room.id}
                            onClick={() => handleOpen(room)}
                            style={{
                                padding: '12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '10px',
                                background: unread > 0 ? '#fff8e1' : 'transparent'
                            }}
                        >
                            <div style={{
                                width: '38px', height: '38px', borderRadius: '50%', background: '#eee',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0
                            }}>👤</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {partnerName}
                                    </span>
                                    <span style={{ fontSize: '10px', color: '#999', flexShrink: 0 }}>
                                        {fmtDateTime(room.updatedAt)}
                                    </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#777', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {room.lastSenderId === 'admin' ? '↩ ' : ''}{room.lastMessage}
                                </div>
                            </div>
                            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                                {unread > 0 && (
                                    <span style={{
                                        background: '#d32f2f', color: 'white', fontSize: '10px', fontWeight: 'bold',
                                        minWidth: '18px', height: '18px', borderRadius: '9px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px'
                                    }}>
                                        {unread}
                                    </span>
                                )}
                                {waiting && unread === 0 && (
                                    <span style={{ fontSize: '9px', color: '#e65100', fontWeight: 'bold' }}>要返信</span>
                                )}
                            </div>
                        </div>
                    );
                })
            )}

            <div style={{ fontSize: '10px', color: '#999', marginTop: '12px', lineHeight: '1.7' }}>
                ※ 直近{LIST_LIMIT}件を表示しています。<br />
                ※ 返信は会員には「運営」と表示されます（担当者名はこの画面でのみ確認できます）。
            </div>
        </div>
    );
};

export default AdminInquiries;
